import router from '@/router';

/**
 * Notification types and their corresponding routes.
 * Used by both in-app notifications (Header) and push notifications (native + web).
 */
export interface NotificationData {
  type?: string;
  user_form_submission_id?: string;
  user_habit_id?: string;
  date?: string;
  conversation_id?: string;
  checkin_id?: string;
  url?: string;
  [key: string]: any;
}

/**
 * Get the route path for a notification based on type and data.
 *
 * IMPORTANT: This logic is shared with public/notification-routes.js
 * for the service worker. When adding new notification types, update both files.
 *
 * @param type - The notification type string
 * @param data - The notification data payload
 * @returns The route path string or null if no route matches
 */
export function getNotificationRoute(type: string | undefined, data: NotificationData | undefined): string | null {
  if (!type && !data) return null;

  // Note shared notifications
  if (type === 'note-shared') {
    return '/account/settings?activeTab=profile&showNotes=true';
  }

  // Questionnaire/Intake form notifications
  if (type === 'questionnaire-created' && data?.user_form_submission_id) {
    return `/intake/${data.user_form_submission_id}`;
  }

  // Check-in notifications
  if (['checkin-created', 'checkin-reviewed', 'form-submission-created', 'checkin-reminder'].includes(type || '')) {
    const id = data?.user_form_submission_id || data?.checkin_id;
    if (id) return `/checkin/${id}`;
  }

  // Workout reminder notifications
  if (type === 'workout-reminder') {
    return '/training';
  }

  // Garmin reconnect notifications: open the Garmin integration modal in settings.
  if (type === 'garmin-workout-reauth') {
    return '/account/settings?activeTab=settings&openModal=integrations';
  }

  // Nutrition reminder notifications
  if (type === 'nutrition-reminder') {
    return '/nutrition';
  }

  // Habit reminder notifications
  if ((type === 'habit-reminder') && data?.user_habit_id) {
    const datePart = data.date ? `?date=${data.date}` : '';
    return `/habit/${data.user_habit_id}${datePart}`;
  }

  // Coach message / chat notifications
  if (type === 'chat-message') {
    const id = data?.conversation_id || data?.chat_id;
    if (id) return `/chat/detail/${id}`;
  }

  // Handle event invitation
  if (data?.event_id) {
    return `/invitation/${data?.event_id}`;
  }

  // Fallback: parse url field from backend
  if (data?.url) {
    try {
      const url = new URL(data.url);
      return url.pathname + url.search;
    } catch {
      if (data.url.startsWith('/')) return data.url;
    }
  }

  return null;
}

/**
 * Navigate to the appropriate page based on notification type and data.
 *
 * @param type - The notification type string
 * @param data - The notification data payload
 * @returns true if navigation was performed, false otherwise
 */
export function navigateToNotification(type: string | undefined, data: NotificationData | undefined): boolean {
  const route = getNotificationRoute(type, data);

  if (route) {
    router.push(route);
    return true;
  }

  return false;
}
